import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useMyOrganizationUsersQuery } from '../adminTools.queries';
import { useUpdateUserRolesMutation } from '../adminTools.mutations';
import type { UserRoleType } from '../../../config/roles';
import type { OrganizationUsersResponse } from '../types/adminTools.types';

export interface UseUserManagementReturn {
  // Dialog state
  dialogOpen: boolean;
  loading: boolean;
  data: OrganizationUsersResponse | null;
  error: string;

  // Edit state
  editingUserId: string | null;
  editedRoles: string[];
  savingRoles: boolean;

  // Actions
  openDialog: () => Promise<void>;
  closeDialog: () => void;
  startEdit: (userId: string, currentRoles: string[]) => void;
  cancelEdit: () => void;
  toggleRole: (role: string) => void;
  saveRoles: (userId: string) => Promise<void>;

  // Callback for InviteUserDialog success
  refreshUsers: () => Promise<void>;
}

export function useUserManagement(): UseUserManagementReturn {
  const { showError, showSuccess } = useErrorHandler();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Server data — fetched only while the dialog is open.
  const usersQuery = useMyOrganizationUsersQuery(dialogOpen);
  const data = usersQuery.data ?? null;
  const loading = dialogOpen && usersQuery.isLoading;
  const error = usersQuery.isError ? 'Failed to fetch users' : '';

  const updateRolesMutation = useUpdateUserRolesMutation();

  // Edit state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editedRoles, setEditedRoles] = useState<string[]>([]);

  const openDialog = useCallback(async () => {
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingUserId(null);
    setEditedRoles([]);
  }, []);

  const startEdit = useCallback((userId: string, currentRoles: string[]) => {
    setEditingUserId(userId);
    setEditedRoles([...currentRoles]);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingUserId(null);
    setEditedRoles([]);
  }, []);

  const toggleRole = useCallback((role: string) => {
    setEditedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  }, []);

  const saveRoles = useCallback(
    async (userId: string) => {
      try {
        // Mutation invalidates the org-users query, refreshing the list.
        await updateRolesMutation.mutateAsync({ userId, roles: editedRoles as UserRoleType[] });

        setEditingUserId(null);
        setEditedRoles([]);
        showSuccess('User roles updated successfully');
      } catch (err) {
        showError(err, 'Failed to update user roles');
      }
    },
    [editedRoles, updateRolesMutation, showError, showSuccess]
  );

  const refreshUsers = useCallback(async () => {
    try {
      await usersQuery.refetch();
      showSuccess('User invited successfully');
    } catch (err) {
      showError(err, 'Failed to refresh users list');
    }
  }, [usersQuery, showError, showSuccess]);

  return {
    dialogOpen,
    loading,
    data,
    error,
    editingUserId,
    editedRoles,
    savingRoles: updateRolesMutation.isPending,
    openDialog,
    closeDialog,
    startEdit,
    cancelEdit,
    toggleRole,
    saveRoles,
    refreshUsers,
  };
}
